import { useMutation } from '@tanstack/react-query';
import {
  CHECK_OUTCOME_LABELS,
  type CheckOutcome,
  type QuoteResponse,
  validationDecisionSchema,
} from '@k-management/shared';
import { useState } from 'react';
import { useCan, useCurrentUser } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';

const OUTCOME_CLASS: Record<CheckOutcome, string> = {
  autonomia: 'ok',
  informato: 'warn',
  convalida: 'bad',
};

/** Once the CFO validated, what asked for it turns green (decision of 05/10/2026). */
function OutcomePill({
  outcome,
  validated = false,
}: {
  outcome: CheckOutcome;
  validated?: boolean;
}) {
  if (outcome === 'convalida' && validated) {
    return <span className="pill ok">Convalidato dal CFO</span>;
  }
  return <span className={`pill ${OUTCOME_CLASS[outcome]}`}>{CHECK_OUTCOME_LABELS[outcome]}</span>;
}

/**
 * The checks of the rules on a preventivo (allegato B §3-§5): each check with
 * its outcome, the validation of the CFO and who approves. The commerciale
 * asks for the validation; the CFO validates or refuses, never on a
 * preventivo of their own.
 */
export function ChecksCard({
  quote,
  onChanged,
}: {
  quote: QuoteResponse;
  onChanged: (updated?: QuoteResponse) => Promise<void>;
}) {
  const user = useCurrentUser();
  const canWrite = useCan('preventivatore.write');
  const [deciding, setDeciding] = useState<'granted' | 'refused' | null>(null);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | undefined>();
  const { checks, validation } = quote;
  // Decisions by role, never by being administrator (decision of 04/10/2026).
  const isCommerciale = quote.owner?.id === user.id;
  const canValidate = quote.you.validate;
  const granted = validation.decision?.outcome === 'granted';
  const refused = validation.decision?.outcome === 'refused';

  const request = useMutation({
    mutationFn: () =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/validation-request`, { method: 'POST' }),
    onSuccess: (updated) => onChanged(updated),
  });
  const decide = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/validation`, { method: 'POST', body }),
    onSuccess: async (updated) => {
      setDeciding(null);
      setNote('');
      await onChanged(updated);
    },
  });

  return (
    <section className="card stack checks-card">
      <div className="row between">
        <h2>Controlli</h2>
        <span className="flags">
          Esito <OutcomePill outcome={checks.outcome} validated={granted} />
        </span>
      </div>
      <ul className="checks-list">
        {checks.checks.map((check) => (
          <li key={check.key}>
            <span className="checks-label">{check.label}</span>
            <span className="muted small">{check.detail}</span>
            <span>
              {check.outcome ? (
                <OutcomePill outcome={check.outcome} validated={granted} />
              ) : check.key === 'amount' ? (
                <span className={`pill ${checks.needsAdApproval ? 'brand' : 'muted'}`}>
                  {checks.needsAdApproval ? 'Approva l’AD' : 'Approva il commerciale'}
                </span>
              ) : checks.complexReasons.length > 0 ? (
                <span className="pill warn">Avviso</span>
              ) : (
                <span className="pill muted">No</span>
              )}
            </span>
            {check.lines.length > 1 && (
              <ul className="checks-lines">
                {check.lines.map((line, index) => (
                  <li key={`${line.label}-${index}`} className="small">
                    {line.label}: {line.value.toLocaleString('it-IT', { maximumFractionDigits: 1 })}
                    % <OutcomePill outcome={line.outcome} validated={granted} />
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {checks.needsValidation && (
        <div className="checks-validation stack tight">
          <strong>
            Convalida del CFO
            <span className="muted small">
              {' '}
              ·{' '}
              {quote.deciders.cfo.length > 0
                ? quote.deciders.cfo.join(', ')
                : 'nessun utente è CFO: assegnalo in Organizzazione › Ruoli'}
            </span>
          </strong>
          {validation.decision ? (
            <p className={refused ? 'notice' : 'notice info'}>
              {granted ? 'Convalidato' : 'Convalida rifiutata'}
              {validation.decision.by && ` da ${validation.decision.by}`} il{' '}
              {formatTimestamp(validation.decision.at)}
              {validation.decision.note && `: «${validation.decision.note}»`}
              {refused && '. Cambia il preventivo e chiedila di nuovo.'}
            </p>
          ) : validation.requested ? (
            <p className="muted small">
              Chiesta{validation.requested.by && ` da ${validation.requested.by}`} il{' '}
              {formatTimestamp(validation.requested.at)}: in attesa del CFO.
            </p>
          ) : (
            <p className="muted small">
              {quote.state === 'bozza'
                ? 'Il preventivo diventa offerta solo dopo la convalida.'
                : 'L’offerta si invia solo dopo la convalida.'}{' '}
              Una modifica successiva la richiede di nuovo.
            </p>
          )}
          <div className="actions start">
            {canWrite && isCommerciale && (!validation.requested || refused) && !granted && (
              <button
                type="button"
                className="primary"
                disabled={request.isPending}
                onClick={() => request.mutate()}
              >
                Chiedi la convalida
              </button>
            )}
            {canValidate && quote.owner?.id !== user.id && !granted && (
              <>
                <button type="button" onClick={() => setDeciding('refused')}>
                  Rifiuta
                </button>
                <button type="button" className="primary" onClick={() => setDeciding('granted')}>
                  Convalida
                </button>
              </>
            )}
          </div>
          {request.isError && <p className="error">{request.error.message}</p>}
        </div>
      )}

      {deciding && (
        <ConfirmDialog
          title={deciding === 'granted' ? 'Convalidare il preventivo?' : 'Rifiutare la convalida?'}
          confirmLabel={deciding === 'granted' ? 'Convalida' : 'Rifiuta'}
          busy={decide.isPending}
          error={decide.error?.message}
          onConfirm={() => {
            const body = { outcome: deciding, note };
            const parsed = validationDecisionSchema.safeParse(body);
            if (!parsed.success) {
              setNoteError(parsed.error.issues[0]?.message);
              return;
            }
            setNoteError(undefined);
            decide.mutate(body);
          }}
          onCancel={() => setDeciding(null)}
        >
          <Field label={deciding === 'granted' ? 'Nota (facoltativa)' : 'Perché'} error={noteError}>
            <textarea
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <p className="muted small">
            Resta registrato con il tuo nome e l’ora. Se il preventivo cambia, serve una nuova
            convalida.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
